import React, { useState, useEffect } from 'react';
import './ProductsPage.css';
import { Link, useLocation } from 'react-router-dom';
import { productCatalog } from './instrumentDetails';

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const ProductsPage = () => {
  const query = useQuery();
  const category = query.get('category');

  const allProducts = React.useMemo(() => {
    const entries = Object.entries(productCatalog).flatMap(([catKey, items]) =>
      items.map((item, idx) => ({
        id: `${catKey}-${idx + 1}`,
        name: item.name,
        category: catKey,
        image: item.image,
        description: `${item.name} — premium quality instrument.`,
      }))
    );
    return entries;
  }, []);

  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (category && productCatalog[category]) {
      setProducts(allProducts.filter(p => p.category === category));
    } else {
      setProducts(allProducts);
    }
  }, [category, allProducts]);

  const filteredProducts = React.useMemo(() => {
    const base = products;
    const q = (search || '').trim().toLowerCase();
    if (!q) return base;
    return base.filter(p => {
      return [p.name, p.category, p.description].some(v => v && v.toLowerCase().includes(q));
    });
  }, [products, search]);

  const handleImageError = (e) => {
    e.target.alt = 'Image not available';
    e.target.style.opacity = '0.2';
    e.target.style.background = '#eee';
    e.target.src = 'data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="180"><rect width="100%" height="100%" fill="#f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#9ca3af" font-family="Arial" font-size="16">Image not available</text></svg>';
  };

  // Safely encode only the filename part to handle spaces and special characters
  const toSafeSrc = (p) => {
    if (!p) return p;
    const i = p.lastIndexOf('/');
    const base = i >= 0 ? p.slice(0, i + 1) : '';
    const name = i >= 0 ? p.slice(i + 1) : p;
    try {
      return base + encodeURIComponent(name);
    } catch {
      return p;
    }
  };

  return (
    <section id="products" className="products-page" /* removed inline top spacing; rely on CSS */>
      <div className="container">
        <div className="products-header-row">
          <div className="section-header" style={{ textAlign: 'left', marginBottom: 0 }}>
            <h2 className="section-title">Our Instruments</h2>
            <p className="section-description">Curated selection — using images from public/images.</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
            <div className="category-filters">
              <Link to="/products">All</Link>
              <Link to="/products?category=general">General</Link>
              <Link to="/products?category=dental">Dental</Link>
              <Link to="/products?category=ophthalmic">Ophthalmic</Link>
              <Link to="/products?category=gynecology_obstetric">Gyne/Obstetric</Link>
              <Link to="/products?category=specialty">Specialty</Link>
            </div>
            {/* moved search input to top center row */}
          </div>
        </div>
        <div className="search-bar-row">
          <input
            type="text"
            className="search-input"
            placeholder="Search instruments..."
            aria-label="Search instruments"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="products-list">
          {filteredProducts.map((product) => (
            <div
              data-product-id={product.id}
              key={product.id}
              style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
            >
              <div style={{ background: '#f7fafc' }}>
                <Link to={`/products/${product.id}`} aria-label={`View details for ${product.name}`}>
                  <img
                    src={toSafeSrc(product.image)}
                    alt={product.name}
                    onError={handleImageError}
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </Link>
              </div>
              <div className="card-content">
                <h3 className="product-name" style={{ margin: '0 0 6px', fontSize: '1rem', fontWeight: 700 }}>
                  <Link to={`/products/${product.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                    {product.name}
                  </Link>
                </h3>
                <p className="product-category" style={{ margin: '0 0 8px', color: '#2563eb', fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{product.category.replace('_',' ')}</p>
                <p className="product-description" style={{ margin: '0 0 12px', color: '#6b7280', fontSize: '0.9rem', lineHeight: 1.5 }}>{product.description}</p>
                <Link to={`/products/${product.id}`} className="view-btn">
                  View Details
                </Link>
              </div>
            </div>
          ))}
        </div>
        {filteredProducts.length === 0 && (
          <div style={{ textAlign: 'center', marginTop: 24, color: 'red', fontWeight: 600 }}>
            No products found — please check your search or data source.
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductsPage;